import { useMemo, useState } from 'react';
import { TriangleAlert, Layers } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@reflex/ui/components/ui/hover-card';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';
import { useBlueprints } from '@/hooks/blueprints/useBlueprints';
import { findRepoBlueprint } from '@/hooks/blueprints/findRepoBlueprint';
import { BlueprintCreateDialog } from '@/components/blueprint/BlueprintCreateDialog';
import type { BlueprintPrefillRepoState } from '@/lib/blueprints';

export interface AgentRepoBlueprintWarningProps {
  /** The `owner/repo` slug the run targets. */
  repoSlug: string;
  /** Branch the run uses, carried into the pre-filled create flow. */
  repoBranch?: string;
  /** How the repo is mounted, carried into the pre-filled create flow. */
  repoAccess?: BlueprintPrefillRepoState['repoAccess'];
  className?: string;
}

/**
 * Shown next to the repo (in the launch dialog's repo chip and the agent
 * header) when no blueprint bakes that repo in: without one, every launch
 * clones the repo and installs its dependencies before the agent starts.
 *
 * The visible element is a small warning icon; hovering (or focusing) it opens
 * a card that explains the cost and holds the "Create blueprint" button, which
 * opens the create flow in a modal pre-filled with the repo — so the blueprint
 * can be built without leaving the run being launched or watched. Keeping the
 * action in the hover card keeps the chip row and header uncluttered.
 *
 * Renders nothing while the blueprint list is loading or failed to load (so it
 * never nudges from unknown/fallback data), or once a matching blueprint exists.
 */
export function AgentRepoBlueprintWarning({
  repoSlug,
  repoBranch,
  repoAccess,
  className,
}: AgentRepoBlueprintWarningProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const { data: blueprints = [], isLoading, isError } = useBlueprints();
  // A blueprint that is still building counts: it will bake the repo in shortly,
  // so nudging again would only produce a duplicate build for the same repo.
  const repoBlueprint = useMemo(
    () => findRepoBlueprint(blueprints, repoSlug, { includeBuilding: true }),
    [blueprints, repoSlug],
  );

  const showNudge = Boolean(repoSlug) && !isLoading && !isError && !repoBlueprint;
  // The nudge disappears the moment a blueprint for the repo shows up in the
  // list — which the poll can do while the create modal is open. Keep the modal
  // mounted through that, or an in-progress edit is discarded without warning
  // and a create that is still in flight loses its success/error handling.
  if (!showNudge && !createOpen) return null;

  const prefillRepo: BlueprintPrefillRepoState = {
    repoSlug,
    ...(repoBranch ? { repoBranch } : {}),
    ...(repoAccess ? { repoAccess } : {}),
  };

  return (
    <>
      {showNudge && (
        <HoverCard openDelay={100} closeDelay={150}>
          <HoverCardTrigger asChild>
            <button
              type="button"
              aria-label="No blueprint for this repo"
              data-testid="repo-no-blueprint-nudge"
              className={cn(
                'inline-flex shrink-0 items-center rounded-xs text-status-accent-orange/90 transition-colors hover:text-status-accent-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                className,
              )}
            >
              <TriangleAlert className="icon-xs" aria-hidden="true" />
            </button>
          </HoverCardTrigger>
          <HoverCardContent
            align="start"
            className="w-72 space-y-2.5"
            data-testid="repo-no-blueprint-card"
          >
            <PageDescription className="mt-0 text-xs leading-relaxed text-foreground/90">
              No blueprint bakes in this repo, so every launch clones it and installs its
              dependencies before the agent starts. Create one to skip that on future launches.
            </PageDescription>
            <Button size="xs" className="w-full gap-1" onClick={() => setCreateOpen(true)}>
              <Layers className="icon-xs" aria-hidden="true" />
              Create Blueprint
            </Button>
          </HoverCardContent>
        </HoverCard>
      )}
      <BlueprintCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        prefillRepo={prefillRepo}
      />
    </>
  );
}
